import { createStyles, makeStyles, Theme, useTheme } from '@material-ui/core'
import { newProjectActions } from 'features/BillOfMaterials/store/newProjectReducer'
import { useDialogManager } from 'features/DialogManager/DialogManager'
import { BoMItemType } from 'model/Project/BoMItemRow'
import { MouseEvent, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useAppDispatch } from 'store/configureStore'
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Label,
  ReferenceLine,
  ResponsiveContainer,
  XAxis,
  YAxis,
} from 'recharts'
import { MoneyDto } from 'services/APIs/InternalAPI/internal-api.contracts'
import { BomItemDetailsDrawerProps } from '../BomItemDetailsDrawer'
import { TargetPriceGraphData } from '../useBomItemDetails'
import { TargetPriceCell } from './TargetPriceCell'
import {
  TargetPriceTooltip,
  TargetPriceTooltipPayload,
  TargetPriceTooltipTypes,
} from './Tooltips/TargetPriceTooltip'
import { XAxisTick } from './XAxisTick'
import { MoneyAxisTick } from './YAxisTick'

export type TargetPriceGraphProps = {
  bomItemId: string
  open: boolean
  targetPrice: MoneyDto
  priceDetails: TargetPriceGraphData[]
  bomItemType: BoMItemType
}

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    graphContainer: {
      height: '100%',
      width: '100%',
      cursor: 'wait',
    },
    discountCell: {
      fill: theme.palette.error.main,
    },
  })
)

export const FinancialDetailsGraph = (props: TargetPriceGraphProps) => {
  const cn = useStyles()
  const { t } = useTranslation()
  const theme = useTheme()
  const dispatch = useAppDispatch()
  const { openDialog } = useDialogManager()

  const [tooltipData, setTooltipData] =
    useState<TargetPriceTooltipPayload>(undefined)

  const mousePosition = useRef({ x: 0, y: 0 })
  const graphContainerRef = useRef<HTMLDivElement>(undefined)

  const handleMouseEnter = (
    e: MouseEvent,
    data: TargetPriceGraphData,
    tooltipDataType: TargetPriceTooltipTypes
  ) => {
    mousePosition.current = { x: e.clientX, y: e.clientY }

    setTooltipData({
      data,
      tooltipDataType,
    })
  }

  const handlePartTypeClicked = (partTypeId: string) => {
    const rechartsWrapper = graphContainerRef.current.querySelectorAll(
      '.recharts-wrapper .cell-wait'
    )

    rechartsWrapper.forEach((e) => (e['style'].cursor = 'wait'))

    requestAnimationFrame(() => {
      dispatch(newProjectActions.setBomItemInSearch(partTypeId))
      dispatch(
        newProjectActions.setOpen({ bomItemId: partTypeId, isOpen: true })
      )
      openDialog('BomItemDetailsDrawer', {
        bomItemId: partTypeId,
        bomItemType:
          props.bomItemType === BoMItemType.project
            ? BoMItemType.assemblyHeader
            : BoMItemType.row,
      } as BomItemDetailsDrawerProps)

      // dispatch(newProjectActions.open)

      if (rechartsWrapper) {
        rechartsWrapper.forEach((e) => (e['style'].cursor = 'pointer'))
      }
    })
  }

  return (
    <>
      {Boolean(tooltipData) && (
        <TargetPriceTooltip
          position={mousePosition.current}
          payload={tooltipData}
          onClose={() => setTooltipData(undefined)}
        />
      )}
      {props.priceDetails?.length && (
        <div className={cn.graphContainer} ref={graphContainerRef}>
          <ResponsiveContainer width="100%" height={204}>
            <BarChart
              data={props.priceDetails}
              style={{
                paddingTop: '1em',
                minWidth: undefined,
                minHeight: undefined,
              }}
            >
              <CartesianGrid strokeDasharray={'3 3'} />
              <XAxis
                dataKey="description"
                type="category"
                tick={
                  <XAxisTick
                    getDataByIndex={(index: number) =>
                      props.priceDetails[index]
                    }
                    onMouseEnter={handleMouseEnter}
                  />
                }
                interval={0}
                tickMargin={12}
                height={42}
              />
              {props.targetPrice?.value && (
                <ReferenceLine
                  y={props.targetPrice.value}
                  stroke={theme.palette.success.dark}
                  strokeWidth={2}
                  // label="target"
                >
                  <Label
                    position="insideBottom"
                    style={{
                      ...theme.typography.caption,
                      fill: theme.palette.text.primary,
                    }}
                  >
                    {t('common:target-price', 'target price')}
                  </Label>
                </ReferenceLine>
              )}

              <YAxis
                type="number"
                ticks={props.priceDetails
                  ?.map((x) => x.salesPrice?.value)
                  .sort()}
                interval={'preserveStartEnd'}
                allowDataOverflow={true}
                // minTickGap={1}
                domain={[
                  0,
                  (maxValue) =>
                    maxValue < props.targetPrice?.value || 0
                      ? props.targetPrice.value
                      : Math.ceil(maxValue),
                ]}
                tick={<MoneyAxisTick defaultMoney={props.targetPrice} />}
              />
              {props.priceDetails.map((x) =>
                x.childrens
                  ? Object.keys(x.childrens).map((key, i) => {
                      return (
                        <Bar
                          key={`childrens.${i}.primary`}
                          dataKey={`childrens.${i}.primary.value`}
                          stackId="a"
                          // fill={x.childrens[key].color}
                          fill={theme.palette.info.dark}
                          stroke={theme.palette.common.black}
                          strokeWidth={1}
                          maxBarSize={54}
                          legendType="none"
                        >
                          <Cell
                            key={`childrens.${x.id}.${i}.cell`}
                            className="cell-wait"
                            onMouseEnter={(e) => {
                              handleMouseEnter(e, x.childrens[key], 'Part')
                            }}
                            onMouseLeave={(e) => {
                              handleMouseEnter(e, undefined, 'Part')
                            }}
                            onClick={() => {
                              handlePartTypeClicked(x.childrens[key].id)
                            }}
                          />
                          {/* <LabelList
                            dataKey={`childrens.${key}.description`}
                            position="right"
                          /> */}
                        </Bar>
                      )
                    })
                  : null
              )}
              <Bar
                dataKey="loading.value"
                stackId="a"
                fill={theme.palette.info.light}
                maxBarSize={54}
              >
                {TargetPriceCell({
                  data: props.priceDetails,
                  onMouseEnter: handleMouseEnter,
                  field: 'loading',
                  tooltipDataType: 'WorkingStep',
                })}
              </Bar>
              <Bar
                dataKey="setup.value"
                stackId="a"
                fill={theme.palette.info.light}
                maxBarSize={54}
              >
                {TargetPriceCell({
                  data: props.priceDetails,
                  onMouseEnter: handleMouseEnter,
                  field: 'setup',
                  tooltipDataType: 'WorkingStep',
                })}
              </Bar>
              <Bar
                dataKey="mainActivityCostPrice.value"
                stackId="a"
                fill={theme.palette.info.main}
                maxBarSize={54}
              >
                {TargetPriceCell({
                  data: props.priceDetails,
                  onMouseEnter: handleMouseEnter,
                  field: 'mainActivityCostPrice',
                  tooltipDataType: 'WorkingStep',
                })}
              </Bar>
              <Bar
                dataKey="unloading.value"
                stackId="a"
                fill={theme.palette.action.active}
                maxBarSize={54}
              >
                {TargetPriceCell({
                  data: props.priceDetails,
                  onMouseEnter: handleMouseEnter,
                  field: 'unloading',
                  tooltipDataType: 'WorkingStep',
                })}
              </Bar>
              <Bar
                dataKey="surchargeValue.value"
                stackId="a"
                fill={theme.palette.success.main}
                maxBarSize={54}
              >
                {props.priceDetails.map((x, index) => (
                  <Cell
                    fill={theme.palette.success.main}
                    key={`cell-${x.description}-${index}-surchargeValue`}
                    onMouseEnter={(e) =>
                      handleMouseEnter(
                        e,
                        {
                          id: x.id,
                          description: x.description,
                          discountValue: x.discountValue,
                          surchargeValue: x.surchargeValue,
                          sharedCost: false,
                          matchDescription: undefined,
                        },
                        'WorkingStep'
                      )
                    }
                    onMouseLeave={(e) =>
                      handleMouseEnter(e, undefined, 'WorkingStep')
                    }
                  />
                ))}
              </Bar>
              <Bar
                dataKey="discountValue.value"
                stackId="a"
                // className={cn.discountCell}
                // stroke={theme.palette.error.main}
                // fill="transparent"
                maxBarSize={54}
              >
                {props.priceDetails.map((x, index) => (
                  <Cell
                    // fill={theme.palette.error.main}
                    // fillOpacity={0.6}
                    className={cn.discountCell}
                    key={`cell-${x.description}-${index}-discountValue`}
                    onMouseEnter={(e) =>
                      handleMouseEnter(
                        e,
                        {
                          id: x.id,
                          description: x.description,
                          discountValue: x.discountValue,
                          surchargeValue: x.surchargeValue,
                          sharedCost: false,
                          matchDescription: undefined,
                        },
                        'WorkingStep'
                      )
                    }
                    onMouseLeave={(e) =>
                      handleMouseEnter(e, undefined, 'WorkingStep')
                    }
                  />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}
    </>
  )
}
